import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchInTheatres,
  fetchThisWeek,
  fetchUpcoming,
} from "../services/movieService";
import { useAuth } from "../contexts/AuthContext";
import ModernLoader from "../components/common/ModernLoader";
import HeroSwiper from "../components/common/HeroSwiper";

import "../styles/home-page.css";

const Home = () => {
  const navigate = useNavigate();
  const [fetchedUpcomingMovies, setFetchedUpcomingMovies] = useState([]);
  const [fetchedWeeklyMovies, setFetchedWeeklyMovies] = useState([]);
  const [fetchedTheatreMovies, setFetchedTheatreMovies] = useState([]);
  const [spotIndex, setSpotIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeLanguage, setActiveLanguage] = useState("All");
  const [onlyNewReleases, setOnlyNewReleases] = useState(false);
  const [onlyReReleases, setOnlyReReleases] = useState(false);
  const [onlyThreeD, setOnlyThreeD] = useState(false);
  const [onlyIceThreeD, setOnlyIceThreeD] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true);
  const releaseFiltersRef = useRef(null);
  const [isFilterFixed, setIsFilterFixed] = useState(false);
  const [filterPlaceholderHeight, setFilterPlaceholderHeight] = useState(0);

  const { user } = useAuth();



  const languageChips = useMemo(() => {
    const languageSet = new Set();
    const combined = [
      ...(fetchedWeeklyMovies || []),
      ...(fetchedTheatreMovies || []),
    ];

    combined.forEach((movie) => {
      if (!movie?.language) return;

      if (Array.isArray(movie.language)) {
        movie.language.forEach((lang) => {
          if (lang) languageSet.add(lang.trim());
        });
        return;
      }

      String(movie.language)
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => languageSet.add(part));
    });

    return ["All", ...Array.from(languageSet).slice(0, 6)];
  }, [fetchedWeeklyMovies, fetchedTheatreMovies]);

  const isNewRelease = (movie) => {
    if (!movie?.startDate) return false;
    const releaseDate = new Date(movie.startDate);
    if (Number.isNaN(releaseDate.getTime())) return false;

    const daysDifference =
      (Date.now() - releaseDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysDifference >= 0 && daysDifference <= 21;
  };

  const isReRelease = (movie) => {
    if (!movie?.startDate) return false;
    const releaseDate = new Date(movie.startDate);
    if (Number.isNaN(releaseDate.getTime())) return false;

    const daysDifference =
      (Date.now() - releaseDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysDifference > 21;
  };

  const movieFormatTokens = (movie) => {
    const raw = [
      movie?.screenType,
      movie?.format,
      movie?.formats,
      movie?.supportedFormats,
    ];

    return raw
      .flatMap((item) => {
        if (!item) return [];
        if (Array.isArray(item)) return item;
        return String(item)
          .split(",")
          .map((part) => part.trim());
      })
      .filter(Boolean)
      .map((value) => value.toLowerCase());
  };

  const hasMovieFormat = (movie, formatText) => {
    const query = formatText.toLowerCase();
    return movieFormatTokens(movie).some((token) => token.includes(query));
  };

  const releaseAndFormatMatch = (movie) => {
    if (onlyNewReleases && !isNewRelease(movie)) return false;
    if (onlyReReleases && !isReRelease(movie)) return false;
    if (onlyThreeD && !hasMovieFormat(movie, "3d")) return false;
    if (
      onlyIceThreeD &&
      !hasMovieFormat(movie, "ice 3d") &&
      !hasMovieFormat(movie, "ice3d")
    )
      return false;
    return true;
  };

  const languageMatch = (movie, selectedLanguage) => {
    if (selectedLanguage === "All") return true;
    if (!movie?.language) return false;

    const normalized = selectedLanguage.toLowerCase();

    if (Array.isArray(movie.language)) {
      return movie.language.some(
        (lang) =>
          String(lang || "")
            .trim()
            .toLowerCase() === normalized,
      );
    }

    return String(movie.language)
      .split(",")
      .map((part) => part.trim().toLowerCase())
      .includes(normalized);
  };

  const weeklyMovies = useMemo(() => {
    // This Week: movies with startDate inside current week (Mon-Sun)
    const startOfWeek = new Date();
    const day = startOfWeek.getDay(); // 0 (Sun) - 6 (Sat)
    const diffToMonday = (day + 6) % 7; // days since Monday
    startOfWeek.setDate(startOfWeek.getDate() - diffToMonday);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return (fetchedWeeklyMovies || []).filter((movie) => {
      if (!languageMatch(movie, activeLanguage)) return false;
      if (!releaseAndFormatMatch(movie)) return false;

      if (!movie?.startDate) return false;
      const sd = new Date(movie.startDate);
      if (Number.isNaN(sd.getTime())) return false;

      return sd >= startOfWeek && sd <= endOfWeek;
    });
  }, [
    fetchedWeeklyMovies,
    activeLanguage,
    onlyNewReleases,
    onlyReReleases,
    onlyThreeD,
    onlyIceThreeD,
  ]);

  const theatreOnlyMovies = useMemo(() => {
    // Only show movies that actually have theaters/shows
    return (fetchedTheatreMovies || [])
      .filter((m) => m && Array.isArray(m.theaters) && m.theaters.length > 0)
      .filter((movie) => {
        if (!languageMatch(movie, activeLanguage)) return false;
        if (!releaseAndFormatMatch(movie)) return false;
        return true;
      })
      .slice(0, 12);
  }, [
    fetchedTheatreMovies,
    activeLanguage,
    onlyNewReleases,
    onlyReReleases,
    onlyThreeD,
    onlyIceThreeD,
  ]);

  // Prepare de-duplicated lists for rendering so the same movie
  // doesn't appear in multiple sections.
  const { weeklyToShow, theatreToShow, upcomingToShow } = useMemo(() => {
    const shownIds = new Set();

    // 1. Prioritize Movies already in Theatres (Primary selling point)
    const t = (theatreOnlyMovies || []).slice(0, 12);
    t.forEach((m) => {
      const id = (m && (m._id || m.id)) || null;
      if (id) shownIds.add(id.toString());
    });

    // 2. Deduped This Week's Releases
    const w = (weeklyMovies || []).filter((m) => {
      const id = (m && (m._id || m.id)) || null;
      return id ? !shownIds.has(id.toString()) : true;
    }).slice(0, 12);

    w.forEach((m) => {
      const id = (m && (m._id || m.id)) || null;
      if (id) shownIds.add(id.toString());
    });

    // 3. Deduped Upcoming Movies
    const u = (fetchedUpcomingMovies || [])
      .filter((movie) => {
        // apply global filters
        if (!languageMatch(movie, activeLanguage)) return false;
        if (!releaseAndFormatMatch(movie)) return false;
        const id = (movie && (movie._id || movie.id)) || null;
        return id ? !shownIds.has(id.toString()) : true;
      })
      .slice(0, 12);

    return { weeklyToShow: w, theatreToShow: t, upcomingToShow: u };
  }, [
    weeklyMovies,
    theatreOnlyMovies,
    fetchedUpcomingMovies,
    activeLanguage,
    onlyNewReleases,
    onlyReReleases,
    onlyThreeD,
    onlyIceThreeD,
  ]);

  const spotlightMovie =
    (weeklyToShow && weeklyToShow[0]) ||
    (theatreToShow && theatreToShow[0]) ||
    (upcomingToShow && upcomingToShow[0]) ||
    null;
  const spotlightPoster =
    spotlightMovie?.poster || "https://via.placeholder.com/1200x640";

  // Available movies: theatre listings that are not already shown in the main sections
  const availableMoviesToShow = useMemo(() => {
    const shown = new Set();
    (weeklyToShow || []).forEach((m) => {
      const id = (m && (m._id || m.id)) || null;
      if (id) shown.add(id.toString());
    });
    (theatreToShow || []).forEach((m) => {
      const id = (m && (m._id || m.id)) || null;
      if (id) shown.add(id.toString());
    });
    (upcomingToShow || []).forEach((m) => {
      const id = (m && (m._id || m.id)) || null;
      if (id) shown.add(id.toString());
    });

    return (fetchedTheatreMovies || [])
      .filter((m) => {
        if (!m || !Array.isArray(m.theaters) || m.theaters.length === 0)
          return false;
        if (!languageMatch(m, activeLanguage)) return false;
        if (!releaseAndFormatMatch(m)) return false;
        const id = (m && (m._id || m.id)) || null;
        return id ? !shown.has(id.toString()) : false;
      })
      .slice(0, 12);
  }, [
    fetchedTheatreMovies,
    weeklyToShow,
    theatreToShow,
    upcomingToShow,
    activeLanguage,
    onlyNewReleases,
    onlyReReleases,
    onlyThreeD,
    onlyIceThreeD,
  ]);

  // Spotlight carousel items (deduped) — used for the video spotlight slider
  const spotlightItems = useMemo(() => {
    const ids = new Set();
    const items = [];

    // Prioritize movies with active shows first
    const primarySource = [
      ...(theatreToShow || []),
      ...(weeklyToShow || []),
      ...(upcomingToShow || []),
    ];

    for (const m of primarySource) {
      if (!m) continue;
      const id = (m._id || m.id)?.toString();
      if (!id || ids.has(id)) continue;
      ids.add(id);
      items.push(m);
      if (items.length >= 6) break;
    }

    // fallback to raw fetched arrays if lists are still dry
    if (items.length < 3) {
      const fallback = [
        ...(fetchedTheatreMovies || []),
        ...(fetchedWeeklyMovies || []),
        ...(fetchedUpcomingMovies || []),
      ];
      for (const m of fallback) {
        if (!m) continue;
        const id = (m._id || m.id)?.toString();
        if (!id || ids.has(id)) continue;
        ids.add(id);
        items.push(m);
        if (items.length >= 6) break;
      }
    }

    return items;
  }, [
    weeklyToShow,
    theatreToShow,
    upcomingToShow,
    fetchedWeeklyMovies,
    fetchedTheatreMovies,
    fetchedUpcomingMovies,
  ]);

  useEffect(() => {
    fetchData();

    const handleUpdate = (event) => {
      const isSync = !!event?.detail;
      console.log(`🔄 Home page update (Sync: ${isSync})`);
      fetchData(isSync);
    };

    window.addEventListener("movie-added", handleUpdate);
    window.addEventListener("movie-updated", handleUpdate);
    window.addEventListener("movies-updated", handleUpdate);
    window.addEventListener("shows-updated", handleUpdate);
    window.addEventListener("theater-added", handleUpdate);
    window.addEventListener("theater-updated", handleUpdate);
    window.addEventListener("theater-deleted", handleUpdate);
    window.addEventListener("theaters-updated", handleUpdate);

    return () => {
      window.removeEventListener("movie-added", handleUpdate);
      window.removeEventListener("movie-updated", handleUpdate);
      window.removeEventListener("movies-updated", handleUpdate);
      window.removeEventListener("shows-updated", handleUpdate);
      window.removeEventListener("theater-added", handleUpdate);
      window.removeEventListener("theater-updated", handleUpdate);
      window.removeEventListener("theater-deleted", handleUpdate);
      window.removeEventListener("theaters-updated", handleUpdate);
    };
  }, [user]);

  useEffect(() => {
    setSpotIndex(0);
  }, [spotlightItems]);

  const currentSpot =
    (spotlightItems && spotlightItems[spotIndex]) || spotlightMovie;
  const currentSpotPoster = currentSpot?.poster || spotlightPoster;

  const fetchData = async (isSync = false) => {
    try {
      if (!isSync) setLoading(true);
      const isAdmin = user && user.role === "admin";

      const [weekRes, theatreRes, upcomingRes] = await Promise.all([
        fetchThisWeek(isAdmin),
        fetchInTheatres(isAdmin),
        fetchUpcoming(isAdmin),
      ]);

      const normalize = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (data.movies && Array.isArray(data.movies)) return data.movies;
        return Object.values(data).filter((item) => item && item.title);
      };

      setFetchedWeeklyMovies(normalize(weekRes));
      setFetchedTheatreMovies(normalize(theatreRes));
      setFetchedUpcomingMovies(normalize(upcomingRes));
    } catch (error) {
      console.error("Error fetching data:", error);
      setFetchedWeeklyMovies([]);
      setFetchedTheatreMovies([]);
      setFetchedUpcomingMovies([]);
    } finally {
      setLoading(false);
    }
  };

  // Fallback sticky behavior: if native sticky doesn't work, toggle fixed positioning
  useEffect(() => {
    const el = releaseFiltersRef.current;
    if (!el) return;

    const computeHeaderOffset = () => {
      const root = document.documentElement;
      const headerVar = getComputedStyle(root).getPropertyValue(
        "--district-header-height",
      );
      const gapVar = getComputedStyle(root).getPropertyValue(
        "--district-sticky-gap",
      );
      const header = parseInt(headerVar, 10) || 98;
      const gap = parseInt(gapVar, 10) || 0;
      return header + gap;
    };

    // Sticky starts exactly when filter strip reaches navbar offset.
    const getFilterTop = () => el.getBoundingClientRect().top;

    const onScroll = () => {
      const top = getFilterTop();
      const threshold = computeHeaderOffset();
      if (top <= threshold) {
        if (!isFilterFixed) {
          setFilterPlaceholderHeight(
            Math.ceil(el.getBoundingClientRect().height),
          );
          setIsFilterFixed(true);
        }
      } else {
        if (isFilterFixed) {
          setIsFilterFixed(false);
          setFilterPlaceholderHeight(0);
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // run once to initialize
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [releaseFiltersRef, isFilterFixed]);

  if (loading) return <ModernLoader text="Loading Movies" />;

  const onMovieClick = (movieId) => {
    if (!movieId) return;
    navigate(`/movie/${movieId}`);
  };

  const hasAnyReleaseFilterActive =
    activeLanguage !== "All" ||
    onlyNewReleases ||
    onlyReReleases ||
    onlyThreeD ||
    onlyIceThreeD;

  const emptyStateText = hasAnyReleaseFilterActive
    ? "No movies found for selected filters."
    : "No movies available right now.";

  const renderReleaseFilters = () => (
    <>
      {isFilterFixed && (
        <div
          aria-hidden="true"
          style={{ height: filterPlaceholderHeight, width: "100%" }}
        />
      )}

      <div
        ref={releaseFiltersRef}
        className={`district-release-filters district-movies-filter-sticky ${isFilterFixed ? "district-release-fixed" : ""}`}
      >
        <div className="district-release-filters-track">
          <button
            type="button"
            className={`district-filter-chip district-filter-trigger ${showAdvancedFilters ? "active" : ""}`}
            onClick={() => setShowAdvancedFilters((prev) => !prev)}
            aria-label="Filters"
          >
            <span className="district-filter-trigger-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13.454 10.7827L7.99927 10.7827"
                  stroke="currentColor"
                  strokeWidth="1.36"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2.5448 4.41797L7.99951 4.41797"
                  stroke="currentColor"
                  strokeWidth="1.36"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.36194 10.7827L2.5437 10.7827"
                  stroke="currentColor"
                  strokeWidth="1.36"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.6368 4.41797H13.4551"
                  stroke="currentColor"
                  strokeWidth="1.36"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <ellipse
                  cx="6.18274"
                  cy="10.7825"
                  rx="1.81824"
                  ry="1.81818"
                  stroke="currentColor"
                  strokeWidth="1.36"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <ellipse
                  cx="1.81824"
                  cy="1.81818"
                  rx="1.81824"
                  ry="1.81818"
                  transform="matrix(-1 0 0 1 11.6343 2.60059)"
                  stroke="currentColor"
                  strokeWidth="1.36"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span>Filters</span>
            <span
              className={`district-filter-trigger-arrow ${showAdvancedFilters ? "open" : ""}`}
              aria-hidden="true"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7.49902 9.74951L11.9998 14.2503L16.5006 9.74951"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>

          {languageChips
            .filter((language) => language !== "All")
            .map((language) => (
              <button
                key={language}
                type="button"
                className={`district-filter-chip ${activeLanguage === language ? "active" : ""}`}
                onClick={() =>
                  setActiveLanguage((prev) =>
                    prev === language ? "All" : language,
                  )
                }
              >
                {language}
              </button>
            ))}

          <button
            type="button"
            className={`district-filter-chip ${onlyNewReleases ? "active" : ""}`}
            onClick={() =>
              setOnlyNewReleases((prev) => {
                const next = !prev;
                if (next) setOnlyReReleases(false);
                return next;
              })
            }
          >
            New Releases
          </button>

          {showAdvancedFilters && (
            <>
              <button
                type="button"
                className={`district-filter-chip ${onlyReReleases ? "active" : ""}`}
                onClick={() =>
                  setOnlyReReleases((prev) => {
                    const next = !prev;
                    if (next) setOnlyNewReleases(false);
                    return next;
                  })
                }
              >
                Re-Releases
              </button>

              <button
                type="button"
                className={`district-filter-chip ${onlyThreeD ? "active" : ""}`}
                onClick={() => setOnlyThreeD((prev) => !prev)}
              >
                3D
              </button>

              <button
                type="button"
                className={`district-filter-chip ${onlyIceThreeD ? "active" : ""}`}
                onClick={() => setOnlyIceThreeD((prev) => !prev)}
              >
                ICE 3D
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );

  const promoBannerUrl =
    "https://b.zmtcdn.com/data/edition_assets/17746128863803.png";

  const faqItems = [
    {
      question: "Catch the trending blockbusters near you",
      answer:
        "Discover top theatrical releases and book tickets instantly from your nearest cinemas. Fresh lineups update regularly, so you always see what is hot this week.",
    },
    {
      question: "Filter by language and new releases",
      answer:
        "Use the sticky filters to switch language and New Releases across Only in Theatres, This Week's Releases, Upcoming Movies, and Available Movies at once.",
    },
    {
      question: "Quick booking from spotlight and cards",
      answer:
        "Click Book now in spotlight or select any movie card to open details and continue with showtime booking in a few steps.",
    },
  ];

  const renderSectionHeading = (title, showAction = false) => (
    <div className="district-section-heading-row">
      <div className="district-section-title-wrap">
        <span className="district-section-line" aria-hidden="true" />
        <h2>{title}</h2>
        <span className="district-section-line" aria-hidden="true" />
      </div>

      {showAction && (
        <button
          type="button"
          className="district-see-all"
          onClick={() => navigate("/movies")}
        >
          See All
        </button>
      )}
    </div>
  );

  return (
    <div className="home-page-container district-home-page">
      <section className="district-home-shell">
        <div className="district-top-banner" aria-hidden="true">
          <img src={promoBannerUrl} alt="Top banner" />
        </div>

        <div className="district-spotlight-carousel-shell">
          <HeroSwiper items={spotlightItems} onMovieClick={onMovieClick} />
        </div>



        {/* Available movies will be rendered after the main release sections */}

        <section className="district-release-section">
          {renderSectionHeading("Only in Theatres")}

          {renderReleaseFilters()}

          {theatreToShow.length > 0 ? (
            <div className="district-movies-grid">
              {theatreToShow.map((movie) => (
                <button
                  type="button"
                  key={`theatre-${movie._id}`}
                  className="district-movie-card"
                  onClick={() => onMovieClick(movie._id)}
                >
                  <img
                    src={movie.poster || "https://via.placeholder.com/220x320"}
                    alt={movie.title}
                  />
                  <div className="district-movie-card-copy">
                    <h5>{movie.title}</h5>
                    <span>{`${movie.certification || "UA"} | ${Array.isArray(movie.language) ? movie.language.join(", ") : movie.language || "Language TBA"}`}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="district-section-empty">
              <h4>Not found</h4>
              <p>{emptyStateText}</p>
            </div>
          )}
        </section>

        <section className="district-release-section">
          {renderSectionHeading("This Week's Releases")}

          {weeklyToShow.length > 0 ? (
            <div className="district-movies-grid">
              {weeklyToShow.map((movie) => (
                <button
                  type="button"
                  key={movie._id}
                  className="district-movie-card"
                  onClick={() => onMovieClick(movie._id)}
                >
                  <img
                    src={movie.poster || "https://via.placeholder.com/220x320"}
                    alt={movie.title}
                  />
                  <div className="district-movie-card-copy">
                    <h5>{movie.title}</h5>
                    <span>{`${movie.certification || "UA"} | ${Array.isArray(movie.language) ? movie.language.join(", ") : movie.language || "Language TBA"}`}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="district-section-empty">
              <h4>Not found</h4>
              <p>{emptyStateText}</p>
            </div>
          )}
        </section>

        <section className="district-release-section">
          {renderSectionHeading("Upcoming Movies")}

          {upcomingToShow.length > 0 ? (
            <div className="district-movies-grid">
              {upcomingToShow.map((movie) => (
                <button
                  type="button"
                  key={`upcoming-${movie._id}`}
                  className="district-movie-card"
                  onClick={() => onMovieClick(movie._id)}
                >
                  <img
                    src={movie.poster || "https://via.placeholder.com/220x320"}
                    alt={movie.title}
                  />
                  <div className="district-movie-card-copy">
                    <h5>{movie.title}</h5>
                    <span>{`${movie.certification || "UA"} | ${Array.isArray(movie.language) ? movie.language.join(", ") : movie.language || "Language TBA"}`}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="district-section-empty">
              <h4>Not found</h4>
              <p>{emptyStateText}</p>
            </div>
          )}
        </section>

        {availableMoviesToShow && availableMoviesToShow.length > 0 && (
          <section className="district-release-section">
            {renderSectionHeading("Available Movies", true)}

            <div className="district-movies-grid">
              {availableMoviesToShow.map((movie) => (
                <button
                  type="button"
                  key={`available-${movie._id}`}
                  className="district-movie-card"
                  onClick={() => onMovieClick(movie._id)}
                >
                  <img
                    src={movie.poster || "https://via.placeholder.com/220x320"}
                    alt={movie.title}
                  />
                  <div className="district-movie-card-copy">
                    <h5>{movie.title}</h5>
                    <span>{`${movie.certification || "UA"} | ${Array.isArray(movie.language) ? movie.language.join(", ") : movie.language || "Language TBA"}`}</span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Partner CTA Section */}
        <section className="district-partner-cta-shell">
          <div className="district-partner-cta-card">
            <div className="district-partner-cta-content">
              <span className="district-tag">For Theater Owners</span>
              <h2>Grow Your Business with District</h2>
              <p>List your theater, manage shows, and reach thousands of movie-goers in your city. Our professional partner dashboard gives you full control.</p>
              <button
                className="district-partner-btn"
                onClick={() => navigate("/partner")}
              >
                Get Started as a Partner →
              </button>
            </div>
            <div className="district-partner-cta-image">
              <img src="https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?q=80&w=2070&auto=format&fit=crop" alt="Cinema Hall" />
            </div>
          </div>
        </section>

        <section className="district-faq-shell" aria-label="Home page guide">
          <h3>Movie Booking Guide</h3>
          <div className="district-faq-list">
            {faqItems.map((item, index) => (
              <details key={`faq-${index}`} className="district-faq-item">
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </section>
    </div>
  );
};

export default Home;
